<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProductTemplatesAPI from 'dashboard/api/productTemplates';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const buildDefaultForm = () => ({
  name: '',
  template_key: '',
  description: '',
  product_name: '',
  product_sku: '',
  product_price: '',
  product_currency: 'USD',
  product_url: '',
  product_image_url: '',
  catalog_id: '',
  product_retailer_id: '',
  source_type: 'json',
  currency: 'USD',
  channel: 'whatsapp',
  mappings: {
    product_name: 'name',
    product_sku: 'sku',
    product_price: 'price',
    product_currency: 'currency',
    product_url: 'url',
  },
});

const isFetching = ref(false);
const isCreating = ref(false);
const isImporting = ref(false);
const showCreateModal = ref(false);
const importFileInput = ref(null);
const records = ref([]);
const form = ref(buildDefaultForm());

const tableHeaders = computed(() => [
  t('PRODUCT_TEMPLATES.LIST.TABLE_HEADER.NAME'),
  t('PRODUCT_TEMPLATES.LIST.TABLE_HEADER.TEMPLATE_KEY'),
  t('PRODUCT_TEMPLATES.LIST.TABLE_HEADER.STATUS'),
  t('PRODUCT_TEMPLATES.LIST.TABLE_HEADER.UPDATED_AT'),
]);

const sourceTypeOptions = computed(() => [
  {
    value: 'json',
    label: t('PRODUCT_TEMPLATES.CREATE.FORM.SOURCE_TYPE_OPTIONS.JSON'),
  },
  {
    value: 'excel',
    label: t('PRODUCT_TEMPLATES.CREATE.FORM.SOURCE_TYPE_OPTIONS.EXCEL'),
  },
]);

const noRecordsFound = computed(
  () => !isFetching.value && !records.value.length
);

const openCreateModal = () => {
  showCreateModal.value = true;
};

const closeCreateModal = () => {
  showCreateModal.value = false;
  form.value = buildDefaultForm();
};

const openImportDialog = () => {
  importFileInput.value?.click();
};

const fetchRecords = async () => {
  isFetching.value = true;
  try {
    const response = await ProductTemplatesAPI.get();
    records.value = response.data;
  } catch {
    useAlert(t('PRODUCT_TEMPLATES.LIST.ERROR_MESSAGE'));
  } finally {
    isFetching.value = false;
  }
};

const buildImportResultMessage = result => {
  const importedCount = result?.imported_count || 0;
  const rejectedCount = result?.rejected_count || 0;

  if (!importedCount && !rejectedCount) {
    return t('PRODUCT_TEMPLATES.IMPORT.API.EMPTY_RESULT');
  }

  if (rejectedCount === 0) {
    return t('PRODUCT_TEMPLATES.IMPORT.API.SUCCESS_MESSAGE', {
      count: importedCount,
    });
  }

  return t('PRODUCT_TEMPLATES.IMPORT.API.PARTIAL_SUCCESS_MESSAGE', {
    imported: importedCount,
    rejected: rejectedCount,
  });
};

const importTemplates = async file => {
  if (!file) return;

  isImporting.value = true;

  try {
    const response = await ProductTemplatesAPI.importTemplates(file);
    useAlert(buildImportResultMessage(response.data));
    await fetchRecords();
  } catch (error) {
    const responseMessage =
      error?.response?.data?.error || error?.response?.data?.message;
    useAlert(
      responseMessage || t('PRODUCT_TEMPLATES.IMPORT.API.ERROR_MESSAGE')
    );
  } finally {
    isImporting.value = false;
  }
};

const onImportFileSelected = async event => {
  const [file] = event.target.files || [];
  await importTemplates(file);
  event.target.value = '';
};

const buildCatalogConfig = () => {
  const sourceType = form.value.source_type?.trim();
  const currency = form.value.currency?.trim().toUpperCase();
  const channel = form.value.channel?.trim();
  const mappings = {
    product_name: form.value.mappings.product_name?.trim(),
    product_sku: form.value.mappings.product_sku?.trim(),
    product_price: form.value.mappings.product_price?.trim(),
    product_currency: form.value.mappings.product_currency?.trim(),
    product_url: form.value.mappings.product_url?.trim(),
  };

  if (
    !sourceType ||
    !currency ||
    !channel ||
    Object.values(mappings).some(value => !value)
  ) {
    useAlert(t('PRODUCT_TEMPLATES.CREATE.API.MISSING_CONFIG_FIELDS'));
    return null;
  }

  return {
    source_type: sourceType,
    currency,
    channel,
    mappings,
  };
};

const createTemplate = async () => {
  const name = form.value.name.trim();
  const templateKey = form.value.template_key.trim();
  const productName = form.value.product_name.trim();
  const productSku = form.value.product_sku.trim();
  const productPrice = form.value.product_price.trim();
  const productCurrency = form.value.product_currency.trim().toUpperCase();
  const productUrl = form.value.product_url.trim();

  if (!name || !templateKey) {
    useAlert(t('PRODUCT_TEMPLATES.CREATE.API.MISSING_FIELDS'));
    return;
  }

  if (
    !productName ||
    !productSku ||
    !productPrice ||
    !productCurrency ||
    !productUrl
  ) {
    useAlert(t('PRODUCT_TEMPLATES.CREATE.API.MISSING_PRODUCT_FIELDS'));
    return;
  }

  isCreating.value = true;

  try {
    const catalogConfig = buildCatalogConfig();
    if (!catalogConfig) {
      return;
    }

    const payload = {
      name,
      template_key: templateKey,
      description: form.value.description.trim(),
      product_name: productName,
      product_sku: productSku,
      product_price: productPrice,
      product_currency: productCurrency,
      product_url: productUrl,
      product_image_url: form.value.product_image_url.trim(),
      catalog_id: form.value.catalog_id.trim(),
      product_retailer_id: form.value.product_retailer_id.trim(),
      active: true,
      catalog_config: catalogConfig,
    };

    const response = await ProductTemplatesAPI.create(payload);
    records.value = [response.data, ...records.value];
    useAlert(t('PRODUCT_TEMPLATES.CREATE.API.SUCCESS_MESSAGE'));
    closeCreateModal();
  } catch (error) {
    const responseMessage =
      error?.response?.data?.message || error?.response?.data?.error;
    const message =
      (Array.isArray(responseMessage) && responseMessage.join(', ')) ||
      (typeof responseMessage === 'string' && responseMessage) ||
      t('PRODUCT_TEMPLATES.CREATE.API.ERROR_MESSAGE');
    useAlert(message);
  } finally {
    isCreating.value = false;
  }
};

const toDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('es-PE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

onMounted(fetchRecords);
</script>

<template>
  <SettingsLayout
    :is-loading="isFetching"
    :loading-message="$t('PRODUCT_TEMPLATES.LOADING')"
    :no-records-found="noRecordsFound"
    :no-records-message="$t('PRODUCT_TEMPLATES.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('PRODUCT_TEMPLATES.HEADER')"
        :description="$t('PRODUCT_TEMPLATES.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-file-up"
            :label="$t('PRODUCT_TEMPLATES.IMPORT.BUTTON')"
            :is-loading="isImporting"
            @click="openImportDialog"
          />
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('PRODUCT_TEMPLATES.HEADER_BTN_TXT')"
            @click="openCreateModal"
          />
        </template>
      </BaseSettingsHeader>

      <input
        ref="importFileInput"
        type="file"
        class="hidden"
        accept=".json,.csv,.xlsx,.xls"
        @change="onImportFileSelected"
      />
    </template>

    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="record in records" :key="record.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="font-medium text-n-slate-12">{{ record.name }}</div>
              <div class="text-xs text-n-slate-10">
                {{ record.product_name || record.description || '-' }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{
                  [record.product_price, record.product_currency]
                    .filter(value => value)
                    .join(' ') || '-'
                }}
              </div>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">{{ record.template_key }}</td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{
                record.active
                  ? $t('PRODUCT_TEMPLATES.STATUS.ACTIVE')
                  : $t('PRODUCT_TEMPLATES.STATUS.INACTIVE')
              }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              {{ toDate(record.updated_at) }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showCreateModal" :on-close="closeCreateModal">
      <div class="flex flex-col h-auto overflow-auto">
        <div class="px-8 pt-6 pb-2">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ $t('PRODUCT_TEMPLATES.CREATE.HEADER') }}
          </h2>
        </div>

        <form class="flex flex-wrap mx-0" @submit.prevent="createTemplate">
          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.NAME_LABEL') }}
            </span>
            <input
              v-model="form.name"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.NAME_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.KEY_LABEL') }}
            </span>
            <input
              v-model="form.template_key"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="$t('PRODUCT_TEMPLATES.CREATE.FORM.KEY_PLACEHOLDER')"
            />
          </label>

          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.DESCRIPTION_LABEL') }}
            </span>
            <input
              v-model="form.description"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.DESCRIPTION_PLACEHOLDER')
              "
            />
          </label>

          <div class="w-full px-8 pb-1">
            <span class="block mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.DEFAULT_PRODUCT_HEADER') }}
            </span>
            <p class="text-xs text-n-slate-10">
              {{
                $t('PRODUCT_TEMPLATES.CREATE.FORM.DEFAULT_PRODUCT_DESCRIPTION')
              }}
            </p>
          </div>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_NAME_LABEL') }}
            </span>
            <input
              v-model="form.product_name"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_NAME_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_SKU_LABEL') }}
            </span>
            <input
              v-model="form.product_sku"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_SKU_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_PRICE_LABEL') }}
            </span>
            <input
              v-model="form.product_price"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_PRICE_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_CURRENCY_LABEL') }}
            </span>
            <input
              v-model="form.product_currency"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_CURRENCY_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_URL_LABEL') }}
            </span>
            <input
              v-model="form.product_url"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_URL_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_IMAGE_URL_LABEL') }}
            </span>
            <input
              v-model="form.product_image_url"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t(
                  'PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_IMAGE_URL_PLACEHOLDER'
                )
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.CATALOG_ID_LABEL') }}
            </span>
            <input
              v-model="form.catalog_id"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.CATALOG_ID_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_RETAILER_ID_LABEL')
              }}
            </span>
            <input
              v-model="form.product_retailer_id"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t(
                  'PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_RETAILER_ID_PLACEHOLDER'
                )
              "
            />
          </label>

          <div class="w-full px-8 pb-4 text-xs text-n-slate-10">
            {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.CATALOG_HELP') }}
          </div>

          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.SOURCE_TYPE_LABEL') }}
            </span>
            <select
              v-model="form.source_type"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            >
              <option
                v-for="sourceTypeOption in sourceTypeOptions"
                :key="sourceTypeOption.value"
                :value="sourceTypeOption.value"
              >
                {{ sourceTypeOption.label }}
              </option>
            </select>
          </label>

          <div class="w-full px-8 pb-4 text-xs text-n-slate-10">
            {{
              form.source_type === 'excel'
                ? $t('PRODUCT_TEMPLATES.CREATE.FORM.SOURCE_TYPE_HELP_EXCEL')
                : $t('PRODUCT_TEMPLATES.CREATE.FORM.SOURCE_TYPE_HELP_JSON')
            }}
          </div>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.CURRENCY_LABEL') }}
            </span>
            <input
              v-model="form.currency"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.CURRENCY_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.CHANNEL_LABEL') }}
            </span>
            <input
              v-model="form.channel"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.CHANNEL_PLACEHOLDER')
              "
            />
          </label>

          <div class="w-full px-8 pb-1">
            <span class="block mb-1 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.MAPPINGS_HEADER') }}
            </span>
            <p class="text-xs text-n-slate-10">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.MAPPINGS_DESCRIPTION') }}
            </p>
          </div>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_NAME_KEY_LABEL') }}
            </span>
            <input
              v-model="form.mappings.product_name"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_NAME_KEY_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_SKU_KEY_LABEL') }}
            </span>
            <input
              v-model="form.mappings.product_sku"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_SKU_KEY_PLACEHOLDER')
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_PRICE_KEY_LABEL') }}
            </span>
            <input
              v-model="form.mappings.product_price"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t(
                  'PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_PRICE_KEY_PLACEHOLDER'
                )
              "
            />
          </label>

          <label class="w-full px-8 pb-4 md:w-1/2">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_CURRENCY_KEY_LABEL')
              }}
            </span>
            <input
              v-model="form.mappings.product_currency"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t(
                  'PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_CURRENCY_KEY_PLACEHOLDER'
                )
              "
            />
          </label>

          <label class="w-full px-8 pb-4">
            <span class="block mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_URL_KEY_LABEL') }}
            </span>
            <input
              v-model="form.mappings.product_url"
              type="text"
              class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
              :placeholder="
                $t('PRODUCT_TEMPLATES.CREATE.FORM.PRODUCT_URL_KEY_PLACEHOLDER')
              "
            />
          </label>

          <div class="flex items-center justify-end w-full gap-2 px-8 py-4">
            <Button
              faded
              slate
              type="reset"
              :label="$t('PRODUCT_TEMPLATES.CREATE.FORM.CANCEL')"
              @click.prevent="closeCreateModal"
            />
            <Button
              type="submit"
              :label="$t('PRODUCT_TEMPLATES.CREATE.FORM.SUBMIT')"
              :is-loading="isCreating"
            />
          </div>
        </form>
      </div>
    </woot-modal>
  </SettingsLayout>
</template>
